import { getDateLocale as getInterfaceDateLocale } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect, useRef } from 'react';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { VideoFeatures, SpeedTest } from '@/store/videoFeatures/types';
import { uuid } from '@/utils/uuid';
import { useVideoFeaturesStore } from '@/store/videoFeatures/store';
import { Zap, ArrowLeftRight, Calendar as CalendarIcon, Activity, Lock, Code, Clock, Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { FinalizeTestAlert } from '@/components/ab-test/FinalizeTestAlert';
import { CopyCodeModal } from '@/components/shared/CopyCodeModal';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useDeleteSpeedTest, useSpeedTestsByUser } from '@/hooks/useSpeedTest';
import { useAuth } from '@/hooks/useAuth';
import { fetchVideoInformations } from '@/utils/generateVideoEmbedCode';

interface AutomaticTestTabProps {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
}

export const AutomaticTestTab: React.FC<AutomaticTestTabProps> = ({
  features,
  setFeature
}) => {
  useLanguage();
  const { addSpeedTest } = useVideoFeaturesStore();
  const { user } = useAuth();
  const { data: speedTests } = useSpeedTestsByUser(user?.id || '', !!user?.id);
  const deleteMutation = useDeleteSpeedTest();
  const [showFinalizeDialog, setShowFinalizeDialog] = useState(false);
  const [showCopyCodeModal, setShowCopyCodeModal] = useState(false);
  const [videoWidth, setVideoWidth] = useState<number | undefined>();
  const [videoHeight, setVideoHeight] = useState<number | undefined>();

  // Buscar dimensões do vídeo
  useEffect(() => {
    const loadVideoDimensions = async () => {
      if (!features.videoId || !user?.id) return;

      try {
        const videoInfo = await fetchVideoInformations(user.id, features.videoId);
        if (videoInfo) {
          setVideoWidth(Number(videoInfo.videoWidth) || 1920);
          setVideoHeight(Number(videoInfo.videoHeight) || 1080);
        }
      } catch (error) {
        // Usar valores padrão 16:9
        setVideoWidth(1920);
        setVideoHeight(1080);
      }
    };

    loadVideoDimensions();
  }, [features.videoId, user?.id]);

  // Estado local para configurações ANTES de ativar o teste
  const [pendingConfig, setPendingConfig] = useState({
    velocidadeInicial: 1.0,
    velocidadeFinal: 1.5,
    endDate: null as string | null,
    endTime: '23:59',
    hasEndDate: false
  });

  const activeTest = features.speedTests?.find(test => test.enabled && test.isAutomatic);
  const isTestActive = !!activeTest;

  // Rola até o card de status (botão "Copiar código") quando o teste sai de
  // 'pending' para 'running' — em telas menores esse card nasce abaixo da dobra
  // e o usuário não percebe que precisa copiar o código. Só dispara na criação
  // (transição pending→running), nunca ao reabrir um teste já em execução.
  const statusCardRef = useRef<HTMLDivElement>(null);
  const prevTestStatusRef = useRef(activeTest?.testStatus);
  useEffect(() => {
    const prev = prevTestStatusRef.current;
    const current = activeTest?.testStatus;
    if (prev === 'pending' && current === 'running') {
      requestAnimationFrame(() => {
        statusCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
    prevTestStatusRef.current = current;
  }, [activeTest?.testStatus]);

  // Verificar expiração: quando o backend remove o teste (lazy expiry por end_date),
  // ele deixa de aparecer na listagem — o frontend detecta a ausência e finaliza localmente
  useEffect(() => {
    if (!activeTest || activeTest.testStatus !== 'running' || !activeTest.endDate) return;
    if (!speedTests) return;

    const stillExistsOnBackend = speedTests.some(
      (test: any) => test.id_video === features.videoId
    );

    if (!stillExistsOnBackend) {
      updateTestConfig('testStatus', 'completed');
      updateTestConfig('enabled', false);
      toast.success(translateInterfaceText("Teste finalizado automaticamente"), {
        description: translateInterfaceText("A data de término foi atingida e o teste foi encerrado.")
      });
    }
  }, [speedTests, activeTest?.testStatus, activeTest?.endDate, features.videoId]);

  // Usar valores do teste ativo se existir, senão usar pendingConfig
  const currentConfig = {
    velocidadeInicial: isTestActive ? (activeTest?.velocidadeInicial ?? 1.0) : pendingConfig.velocidadeInicial,
    velocidadeFinal: isTestActive ? (activeTest?.velocidadeFinal ?? 1.5) : pendingConfig.velocidadeFinal,
    endDate: isTestActive ? activeTest?.endDate : pendingConfig.endDate,
    endTime: pendingConfig.endTime,
    hasEndDate: isTestActive ? !!activeTest?.endDate : (pendingConfig.hasEndDate ?? false)
  };


  const handleToggleSpeedTest = (enabled: boolean) => {
    if (enabled) {
      // Apenas marca que o recurso está ativo, criará o teste no backend ao clicar em "Aplicar"
      const newTest: SpeedTest = {
        id: uuid(),
        videoId: features.videoId,
        velocidade: 1.0,
        date: new Date().toISOString(),
        enabled: true,
        isAutomatic: true,
        velocidadeInicial: pendingConfig.velocidadeInicial,
        velocidadeFinal: pendingConfig.velocidadeFinal,
        endDate: pendingConfig.hasEndDate ? pendingConfig.endDate : undefined,
        duracaoMinimaDias: 7,
        criterioVitoria: 'playRate',
        testStatus: 'pending', // Marca como pendente até criar no backend
        manterEmbedAtual: false,
      };
      addSpeedTest(newTest);
    } else {
      // Remove automatic test
      const updatedTests = features.speedTests?.filter(test => !(test.enabled && test.isAutomatic)) || [];
      setFeature('speedTests', updatedTests);
    }
  };

  const updateTestConfig = (field: keyof SpeedTest, value: any) => {
    if (!activeTest) return;
    
    const updatedTests = features.speedTests?.map(test => 
      test.id === activeTest.id ? { ...test, [field]: value } : test
    ) || [];
    
    setFeature('speedTests', updatedTests);
  };

  // Handler unificado para alterações de config (funciona antes e depois de ativar)
  const handleConfigChange = (field: string, value: any) => {
    if (isTestActive) {
      updateTestConfig(field as keyof SpeedTest, value);
    } else {
      setPendingConfig(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleFinalSpeedChange = (value: string) => {
    const finalSpeed = parseFloat(value);
    const initialSpeed = currentConfig.velocidadeInicial || 1.0;

    if (finalSpeed <= initialSpeed) {
      toast.warning(translateInterfaceText("Ajustamos o intervalo para manter a ordem correta."));
      handleConfigChange('velocidadeFinal', initialSpeed + 0.1);
    } else {
      handleConfigChange('velocidadeFinal', finalSpeed);
    }
  };

  return (
    <div className="space-y-4">
      {/* Toggle: Ativar teste automático (primeira seção, logo após tabs) */}
      <div
        className={cn(
          "rounded-xl p-4",
          "transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]",
          isTestActive
            ? "bg-[#EEF4FF] shadow-[0_0_0_1px_rgba(31,107,255,0.1)]"
            : "bg-[#F7F8FE]"
        )}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={cn(
              "flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]",
              isTestActive
                ? "bg-[#1D4FFF]/10 text-[#1D4FFF]"
                : "bg-[#E8F0FF] text-[#6B7280]"
            )}>
              <Zap className="w-4 h-4" strokeWidth={2} />
            </div>
            <div>
              <span className="text-sm font-medium text-[#1C1C1E]">{translateInterfaceText("Ativar teste automático")}</span>
            </div>
          </div>
          <Switch
            checked={isTestActive}
            onCheckedChange={handleToggleSpeedTest}
            disabled={activeTest?.testStatus === 'running'}
            className="data-[state=checked]:bg-[#1F6BFF] disabled:cursor-not-allowed disabled:opacity-100"
          />
        </div>

        <p className="text-[13px] text-[#6B7280] leading-relaxed pl-12 mt-1">
          {isTestActive && activeTest?.testStatus === 'running'
            ? translateInterfaceText("O teste está coletando dados em tempo real")
            : isTestActive && activeTest?.testStatus === 'pending'
            ? translateInterfaceText("Configurações prontas. Clique em \"Aplicar\" para iniciar o teste.")
            : translateInterfaceText("Configure e ative o teste para coletar dados")
          }
        </p>
      </div>

      {/* Aviso para clicar em Aplicar (quando pendente) */}
      {isTestActive && activeTest?.testStatus === 'pending' && (
        <div className="flex items-center gap-2 p-3 bg-[#FFF9E6] border border-[#FFE4A3] rounded-lg">
          <Info className="h-4 w-4 text-[#F59E0B] flex-shrink-0" />
          <span className="text-xs text-[#92400E]">
            <strong>{translateInterfaceText("Atenção:")}</strong> {" "}{translateInterfaceText("As configurações ainda não foram salvas. Clique no botão \"Aplicar\" no canto superior direito para iniciar o teste.")}</span>
        </div>
      )}

      {/* Card 1: Configuração de Velocidades (sempre visível) */}
      <Card className="border-[#E6E6E6] rounded-xl shadow-[0px_2px_6px_rgba(0,0,0,0.06)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-[#1C1C1E] flex items-center gap-2">
            <ArrowLeftRight className="h-4 w-4 text-[#6F6F75]" />
            {translateInterfaceText("Intervalo de Velocidades")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-[#6F6F75]">{translateInterfaceText("Velocidade inicial")}</label>
              <Select
                value={currentConfig.velocidadeInicial?.toString() || "1.0"}
                onValueChange={(value) => handleConfigChange('velocidadeInicial', parseFloat(value))}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1.0, 1.1, 1.2, 1.3, 1.4].map(speed => (
                    <SelectItem key={speed} value={speed.toString()}>
                      {speed.toFixed(1)}x
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-[#6F6F75]">{translateInterfaceText("Velocidade final")}</label>
              <Select
                value={currentConfig.velocidadeFinal?.toString() || "1.5"}
                onValueChange={handleFinalSpeedChange}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1.1, 1.2, 1.3, 1.4, 1.5].map(speed => (
                    <SelectItem key={speed} value={speed.toString()}>
                      {speed.toFixed(1)}x
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Card 2: Duração do Teste (sempre visível) */}
      <Card className="border-[#E6E6E6] rounded-xl shadow-[0px_2px_6px_rgba(0,0,0,0.06)]">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-4 w-4 text-[#1F6BFF]" />
            <CardTitle className="text-sm font-semibold text-[#1C1C1E]">{translateInterfaceText("Duração do Teste")}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Radio oculto para o grupo funcionar */}
          <input
            type="radio"
            name="speed-test-duration"
            value="with-end"
            checked={currentConfig.hasEndDate === true}
            onChange={() => {}}
            className="hidden"
          />

          {/* Radio: Sem término */}
          <div
            className="flex items-start gap-3 p-3 rounded-lg border border-[#E6E6E6] hover:border-[#CBD5E1] transition-colors cursor-pointer"
            onClick={() => {
              handleConfigChange('hasEndDate', false);
              handleConfigChange('endDate', undefined);
            }}
          >
            <input
              type="radio"
              id="speed-no-end-date"
              name="speed-test-duration"
              value="no-end"
              checked={!currentConfig.hasEndDate}
              onChange={() => {
                handleConfigChange('hasEndDate', false);
                handleConfigChange('endDate', undefined);
              }}
              className="mt-0.5 h-4 w-4 text-[#1F6BFF] focus:ring-[#1F6BFF]"
            />
            <div className="flex-1">
              <label htmlFor="speed-no-end-date" className="text-sm font-medium cursor-pointer text-[#1C1C1E]">
                {translateInterfaceText("Sem término (manual)")}</label>
              <p className="text-xs text-[#6F6F75] mt-0.5">
                {translateInterfaceText("O teste só encerra quando você clicar em \"Finalizar Teste\".")}</p>
            </div>
          </div>

          {/* Radio: Finalizar automaticamente */}
          <div
            className="flex items-start gap-3 p-3 rounded-lg border border-[#E6E6E6] hover:border-[#CBD5E1] transition-colors cursor-pointer"
            onClick={() => {
              if (!currentConfig.hasEndDate) {
                const defaultEndDate = new Date();
                defaultEndDate.setDate(defaultEndDate.getDate() + 7);
                handleConfigChange('hasEndDate', true);
                handleConfigChange('endDate', defaultEndDate.toISOString());
              }
            }}
          >
            <input
              type="radio"
              id="speed-with-end-date"
              name="speed-test-duration"
              checked={currentConfig.hasEndDate === true}
              onChange={() => {
                const defaultEndDate = new Date();
                defaultEndDate.setDate(defaultEndDate.getDate() + 7);
                handleConfigChange('hasEndDate', true);
                handleConfigChange('endDate', defaultEndDate.toISOString());
              }}
              className="mt-0.5 h-4 w-4 text-[#1F6BFF] focus:ring-[#1F6BFF]"
            />
            <div className="flex-1">
              <label htmlFor="speed-with-end-date" className="text-sm font-medium cursor-pointer text-[#1C1C1E]">
                {translateInterfaceText("Finalizar automaticamente")}</label>
              <p className="text-xs text-[#6F6F75] mt-0.5">
                {currentConfig.hasEndDate && currentConfig.endDate
                  ? `${format(new Date(currentConfig.endDate), "dd MMM yyyy", { locale: getInterfaceDateLocale() })} · ${currentConfig.endTime || '23:59'}`
                  : translateInterfaceText("Defina uma data para encerrar o teste e selecionar a melhor velocidade.")
                }
              </p>

              {currentConfig.hasEndDate && (
                <div className="mt-3 space-y-4" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-3">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9 w-[160px] justify-start px-3 text-sm gap-2.5 border-border hover:border-primary hover:bg-primary/5 hover:text-foreground transition-colors font-normal"
                        >
                          <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                          {currentConfig.endDate
                            ? format(new Date(currentConfig.endDate), "dd/MM/yyyy")
                            : translateInterfaceText("Selecione")
                          }
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={currentConfig.endDate ? new Date(currentConfig.endDate) : undefined}
                          onSelect={(date) => {
                            if (date && date >= new Date()) {
                              const [hours, minutes] = (currentConfig.endTime || '23:59').split(':');
                              date.setHours(parseInt(hours), parseInt(minutes));
                              handleConfigChange('endDate', date.toISOString());
                            }
                          }}
                          disabled={(date) => date < new Date()}
                          className="pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>

                    <div className="flex items-center gap-2 px-3 h-9 w-[120px] border border-border rounded-md hover:border-primary transition-colors bg-background">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <input
                        type="time"
                        value={currentConfig.endTime || '23:59'}
                        onChange={(e) => {
                          handleConfigChange('endTime', e.target.value);
                          if (currentConfig.endDate) {
                            const [hours, minutes] = e.target.value.split(':');
                            const newDate = new Date(currentConfig.endDate);
                            newDate.setHours(parseInt(hours), parseInt(minutes));
                            handleConfigChange('endDate', newDate.toISOString());
                          }
                        }}
                        className="text-sm bg-transparent border-0 focus:outline-none flex-1 text-foreground"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Card 3: Status do Teste (só quando ativo E com status 'running') */}
      {isTestActive && activeTest && activeTest.testStatus === 'running' && (
        <Card ref={statusCardRef} className="border-[#E6E6E6] rounded-xl shadow-[0px_2px_6px_rgba(0,0,0,0.06)] bg-gradient-to-br from-[#F8FAFF] to-white">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-[#1F6BFF]" />
                <CardTitle className="text-sm font-semibold text-[#1C1C1E]">{translateInterfaceText("Status do Teste")}</CardTitle>
              </div>
              <Badge className="bg-[#1F6BFF] text-white hover:bg-[#1F6BFF]/90 gap-1 text-[10px] px-2 py-0.5">
                <Clock className="h-2.5 w-2.5" />
                {translateInterfaceText("Em execução")}</Badge>
            </div>
            <p className="text-[11px] text-[#6F6F75] mt-1">
              {translateInterfaceText("Coletando dados em tempo real")}</p>
          </CardHeader>
          <CardContent className="space-y-3 pt-0">
            {/* Informações do Teste */}
            <div className="space-y-2 py-2">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-[#6F6F75]">{translateInterfaceText("Intervalo testado:")}</span>
                <span className="font-semibold text-[#1C1C1E]">
                  {activeTest.velocidadeInicial}x
                </span>
                <ArrowLeftRight className="h-3.5 w-3.5 text-[#1F6BFF]" />
                <span className="font-semibold text-[#1C1C1E]">
                  {activeTest.velocidadeFinal}x
                </span>
              </div>

              {activeTest.endDate && (
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-3.5 w-3.5 text-[#6F6F75]" />
                  <span className="text-[#6F6F75]">{translateInterfaceText("Término previsto:")}</span>
                  <span className="font-semibold text-[#1C1C1E]">
                    {format(new Date(activeTest.endDate), "dd/MM/yyyy", { locale: getInterfaceDateLocale() })}
                  </span>
                </div>
              )}
            </div>

            {/* Botões de Ação - Compactos */}
            <div className="flex gap-3 pt-1">
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={() => setShowCopyCodeModal(true)}
                      className="flex-1 gap-2 h-[38px] px-[18px] rounded-xl font-semibold text-sm shadow-lg transition-all duration-200 border-0"
                      size="sm"
                      style={{
                        background: 'linear-gradient(135deg, #2D6CF6 0%, #1E5FE6 100%)',
                        color: '#FFFFFF',
                        boxShadow: '0 4px 12px rgba(45, 108, 246, 0.3)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(45, 108, 246, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(45, 108, 246, 0.3)';
                      }}
                    >
                      <Code className="h-[18px] w-[18px]" />
                      {translateInterfaceText("Copiar código")}</Button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    align="center"
                    className="bg-gray-900 text-white text-xs px-3 py-1.5"
                  >
                    {translateInterfaceText("Copiar código de incorporação do vídeo")}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowFinalizeDialog(true)}
                className="flex-1 gap-2 border-[#E44848] text-[#E44848] hover:bg-[#DC2626] hover:text-white hover:border-[#DC2626] h-9 text-sm"
              >
                <Lock className="h-3.5 w-3.5" />
                {translateInterfaceText("Finalizar Teste")}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal de Finalizar Teste */}
      <FinalizeTestAlert
        open={showFinalizeDialog}
        onOpenChange={setShowFinalizeDialog}
        onConfirm={async () => {
          if (activeTest) {
            // Fallback pro id local: logo após criar, o cache do hook está stale e
            // realTest vem undefined — sem isso o delete não achava o id_speed_test.
            const realTest = speedTests?.find(test => (test as any).id_video === features.videoId);
            const speedTestId = (realTest as any)?.id_speed_test || activeTest.id;

            try {
              if (speedTestId) {
                await deleteMutation.mutateAsync(speedTestId);
              }
              updateTestConfig('testStatus', 'completed');
              updateTestConfig('enabled', false);
              toast.success(translateInterfaceText("Teste finalizado"), {
                description: translateInterfaceText("Teste de velocidade deletado com sucesso.")
              });
            } catch (error) {
              toast.error(translateInterfaceText("Erro ao finalizar teste"), {
                description: translateInterfaceText("Não foi possível deletar o teste. Tente novamente.")
              });
            }
          }
          setShowFinalizeDialog(false);
        }}
      />

      {/* Modal de Copiar Código — usa videoOrigin=SPEEDTEST para gerar embed do teste */}
      {features.videoId && (
        <CopyCodeModal
          isOpen={showCopyCodeModal}
          onClose={() => setShowCopyCodeModal(false)}
          entityType="video"
          entityId={features.videoId}
          entityName="Teste de Velocidade"
          userId={user?.id || user?.id_user}
          videoOrigin="SPEEDTEST"
          hideJsTab={true}
        />
      )}
    </div>
  );
};
